import { createServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";

/** Search + filtering over the vehicle database (public read-only). */

const sel = (s: string): string => s;

export interface SearchResultRow {
  id: string;
  year: number;
  trim: string | null;
  transmission: string | null;
  drive: string | null;
  vehicle_class: string | null;
  engine_desc: string | null;
  cylinders: string | null;
  displacement: string | null;
  mpg_combined: number | null;
  mpg_city: number | null;
  mpg_hwy: number | null;
  annual_fuel_cost: number | null;
  /** How many rows exist for this model + year (trim/engine variants). */
  configCount: number;
  models: {
    name: string;
    slug: string;
    makes: { name: string; slug: string };
  };
}

export interface SearchFilterOptions {
  makes: { name: string; slug: string }[];
  models: { name: string; slug: string }[];
  years: number[];
}

export const SORT_OPTIONS = [
  { value: "az", label: "A–Z (make, model)" },
  { value: "year", label: "Newest model year" },
  { value: "mpg", label: "Best combined MPG" },
  { value: "fuel-cost", label: "Lowest annual fuel cost" },
] as const;

export type SortKey = (typeof SORT_OPTIONS)[number]["value"];

export const DEFAULT_SORT: SortKey = "az";

export function isSortKey(v: string): v is SortKey {
  return SORT_OPTIONS.some((o) => o.value === v);
}

export interface SearchInput {
  q?: string;
  make?: string;
  model?: string;
  year?: number;
  dataType?: string;
  sort?: string;
  page?: number;
}

const PAGE_SIZE = 25;


/** Options used to populate the filter dropdowns. Models depend on the selected make. */
export const getSearchFilterOptions = createServerFn({ method: "GET" })
  .inputValidator((data: { make?: string }) => data)
  .handler(async ({ data }): Promise<SearchFilterOptions> => {
    const [{ data: makes }, { data: newest }, { data: oldest }] = await Promise.all([
      supabase.from("makes").select("name, slug").order("name"),
      supabase.from("vehicles").select("year").order("year", { ascending: false }).limit(1),
      supabase.from("vehicles").select("year").order("year", { ascending: true }).limit(1),
    ]);

    let models: { name: string; slug: string }[] = [];
    if (data.make) {
      const { data: makeRow } = await supabase
        .from("makes")
        .select("id")
        .eq("slug", data.make)
        .maybeSingle();
      if (makeRow) {
        const { data: modelRows } = await supabase
          .from("models")
          .select("name, slug")
          .eq("make_id", makeRow.id)
          .order("name");
        models = modelRows ?? [];
      }
    }

    const maxYear = newest?.[0]?.year;
    const minYear = oldest?.[0]?.year;
    const years: number[] = [];
    if (maxYear && minYear) {
      for (let y = maxYear; y >= minYear; y--) years.push(y);
    }

    return { makes: makes ?? [], models, years };
  });

interface RpcRow {
  id: string;
  year: number;
  trim_name: string | null;
  transmission: string | null;
  drive: string | null;
  vehicle_class: string | null;
  engine_desc: string | null;
  cylinders: string | null;
  displacement: string | null;
  mpg_combined: number | null;
  mpg_city: number | null;
  mpg_hwy: number | null;
  annual_fuel_cost: number | null;
  model_name: string;
  model_slug: string;
  make_name: string;
  make_slug: string;
  config_count: number;
  total_count: number;
}

/**
 * Run a filtered vehicle search. Ordering, filtering and counting all happen in
 * one database call so paging is stable and the default order is defined
 * (alphabetical by make then model) rather than physical row order.
 */
export const searchVehicles = createServerFn({ method: "GET" })
  .inputValidator((data: SearchInput) => data)
  .handler(async ({ data }) => {
    const page = Math.max(1, data.page ?? 1);
    const sort: SortKey = data.sort && isSortKey(data.sort) ? data.sort : DEFAULT_SORT;
    const empty = { results: [] as SearchResultRow[], total: 0, page, pageSize: PAGE_SIZE, sort };

    const { data: rows, error } = await supabase.rpc("search_vehicles_page", {
      p_q: (data.q ?? "").replace(/[%,()]/g, " ").trim(),
      p_make: data.make ?? "",
      p_model: data.model ?? "",
      ...(data.year ? { p_year: data.year } : {}),
      p_datatype: data.dataType ?? "",
      p_sort: sort,
      p_limit: PAGE_SIZE,
      p_offset: (page - 1) * PAGE_SIZE,
    });

    if (error) {
      console.error("searchVehicles error:", error);
      return empty;
    }

    const list = (rows ?? []) as unknown as RpcRow[];
    const results: SearchResultRow[] = list.map((r) => ({
      id: r.id,
      year: r.year,
      trim: r.trim_name,
      transmission: r.transmission,
      drive: r.drive,
      vehicle_class: r.vehicle_class,
      engine_desc: r.engine_desc,
      cylinders: r.cylinders,
      displacement: r.displacement,
      mpg_combined: r.mpg_combined,
      mpg_city: r.mpg_city,
      mpg_hwy: r.mpg_hwy,
      annual_fuel_cost: r.annual_fuel_cost,
      configCount: Number(r.config_count ?? 1),
      models: {
        name: r.model_name,
        slug: r.model_slug,
        makes: { name: r.make_name, slug: r.make_slug },
      },
    }));

    return {
      results,
      total: Number(list[0]?.total_count ?? 0),
      page,
      pageSize: PAGE_SIZE,
      sort,

    };
  });
